// Mitarbeiter-Detailseite (design_handoff_team §4) – eigene Route /admin/team/:mitarbeiterId statt Modal.
// Fachlogik wie im bisherigen TeamMitarbeiterDetailsModal: Module aus portal_modules (aktiv), allowed_modules
// speichern (leer ⇒ ['dashboard']), Löschen per RPC delete_user_completely – Rückfall auf profiles.delete nur,
// wenn die Funktion fehlt. Gebaut so, dass sie später in einer zentralen Kontakt-/Profilseite aufgehen kann.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Buildings, CalendarBlank, CalendarCheck, ChatCircleDots, ClipboardText, FileText, IdentificationBadge, IdentificationCard, PaperPlaneTilt, UserCircle, CaretLeft, CaretRight, CircleNotch, EnvelopeSimple, FloppyDisk, FolderSimple, Gauge, Fingerprint,
  Key, Layout, LockKey, PencilSimple, Receipt, Shield, ShieldCheck, Sparkle, SquaresFour, Ticket, Timer, Trash, User, Users,
  UsersThree, WarningCircle, Wrench, type Icon,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { rechteText, rollenName, startModule, statusText, type PortalModul, type TeamMitglied } from '../../../lib/team/mitarbeiter';
import { useAppMeldungen } from '../../../components/ui/AppMeldungen';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { IosSchalter } from '../../../components/ui/IosSchalter';
import { MitarbeiterDialog, MitarbeiterLoeschenDialog } from './teile/MitarbeiterDialoge';
import { FehlerKarte, LadeKarte, MitarbeiterAvatar, statusFarbe } from './teile/Bausteine';

type Ansicht = 'details' | 'berechtigungen';

const MODUL_ICON: Record<string, Icon> = {
  dashboard: SquaresFour, objekte: Buildings, immobilien: Buildings, mieter: Users, eigentuemer: Key, dienstleister: Wrench,
  rechnungen: Receipt, tickets: Ticket, dokumente: FolderSimple, emails: EnvelopeSimple, zeiterfassung: Timer, zaehler: Gauge,
  kalender: CalendarBlank, team: UsersThree, mieterselbstauskunft: IdentificationCard, uebergabeprotokolle: ClipboardText,
  abwesenheiten: CalendarCheck, monatsabschluss: LockKey, personalakte: IdentificationBadge, serienmail: PaperPlaneTilt,
  chat: ChatCircleDots, profile: UserCircle, vertraege: FileText,
};

const fehlerText = (err: unknown): string => (err instanceof Error ? err.message : (err as { message?: string } | null)?.message ?? String(err));

const glasKnopf = { height: 36, padding: '0 14px 0 10px', gap: 6, borderRadius: 18, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, color: 'var(--ink)' } as const;

export default function MitarbeiterDetailSeite() {
  const { mitarbeiterId } = useParams<{ mitarbeiterId: string }>();
  const navigate = useNavigate();
  const { mobil, schmal } = useMobilModus();
  const { melde, dialoge } = useAppMeldungen(mobil);

  const [m, setM] = useState<TeamMitglied | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [ansicht, setAnsicht] = useState<Ansicht>('details');
  const [module, setModule] = useState<PortalModul[]>([]);
  const [moduleLaden, setModuleLaden] = useState(false);
  const [erlaubt, setErlaubt] = useState<string[]>([]);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [loescht, setLoescht] = useState(false);
  const [bearbeitenOffen, setBearbeitenOffen] = useState(false);

  const zurListe = useCallback(() => navigate('/admin/team'), [navigate]);

  const laden = useCallback(async () => {
    if (!mitarbeiterId) return;
    setLaedt(true);
    setLadeFehler(null);
    const { data, error } = await supabase.from('profiles').select('*').eq('id', mitarbeiterId).maybeSingle();
    if (error) setLadeFehler(error.message);
    else {
      const p = (data ?? null) as TeamMitglied | null;
      setM(p);
      if (p) setErlaubt(startModule(p.allowed_modules));
    }
    setLaedt(false);
  }, [mitarbeiterId]);

  useEffect(() => { void laden(); }, [laden]);

  useEffect(() => {
    setModuleLaden(true);
    void supabase.from('portal_modules').select('*').eq('is_active', true).then(({ data, error }) => {
      if (error) console.error('Fehler beim Laden der Module:', error);
      else setModule((data ?? []) as PortalModul[]);
      setModuleLaden(false);
    });
  }, []);

  // Esc: offener Dialog schließt sich selbst, sonst zurück zur Liste.
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || loeschenOffen || bearbeitenOffen || loescht) return;
      if (document.querySelector('[aria-modal="true"], [role="menu"]')) return;
      zurListe();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [loeschenOffen, bearbeitenOffen, loescht, zurListe]);

  const zeige = (a: Ansicht) => {
    setFehler(null);
    // Beim Öffnen der Berechtigungen gelten die gespeicherten Module (leer ⇒ dashboard).
    if (a === 'berechtigungen' && m) setErlaubt(startModule(m.allowed_modules));
    setAnsicht(a);
  };

  const umschalten = (id: string) => setErlaubt((alt) => (alt.includes(id) ? alt.filter((x) => x !== id) : [...alt, id]));

  const sichern = async () => {
    if (!m) return;
    setSpeichert(true);
    setFehler(null);
    const { error } = await supabase.from('profiles').update({ allowed_modules: erlaubt }).eq('id', m.id);
    if (error) setFehler('Fehler beim Speichern der Rechte: ' + error.message);
    else { await laden(); setAnsicht('details'); }
    setSpeichert(false);
  };

  const loeschen = async () => {
    if (!m) return;
    setLoescht(true);
    try {
      const { error: rpcError } = await supabase.rpc('delete_user_completely', { user_id: m.id });
      if (rpcError) {
        if (rpcError.message.includes('function') && rpcError.message.includes('does not exist')) {
          const { error: profileError } = await supabase.from('profiles').delete().eq('id', m.id);
          if (profileError) throw profileError;
        } else {
          throw rpcError;
        }
      }
      setLoeschenOffen(false);
      zurListe();
    } catch (err) {
      console.error('Löschfehler:', err);
      setLoeschenOffen(false);
      melde(fehlerText(err) || 'Fehler beim vollständigen Löschen.', true);
    } finally {
      setLoescht(false);
    }
  };

  const brotkrumen = (
    <nav aria-label="Brotkrumen" className="flex flex-wrap items-center" style={{ gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>
      <button type="button" onClick={zurListe} data-tip="Zurück – Zur Mitarbeiterliste" className="immo-glas inline-flex items-center" style={{ ...glasKnopf, height: mobil ? 44 : 36, borderRadius: mobil ? 22 : 18 }}>
        <CaretLeft size={14} weight="bold" />Teamverwaltung
      </button>
      <CaretRight size={11} weight="bold" aria-hidden /><span>Mitarbeiter</span>
      {m && <><CaretRight size={11} weight="bold" aria-hidden /><span aria-current="page" style={{ color: 'var(--ink)' }}>{m.full_name}</span></>}
    </nav>
  );

  if (laedt && !m) return <div className="flex flex-col" style={{ gap: 14 }}>{brotkrumen}<LadeKarte /></div>;
  if (ladeFehler) return <div className="flex flex-col" style={{ gap: 14 }}>{brotkrumen}<FehlerKarte meldung={ladeFehler} onErneut={() => void laden()} /></div>;
  if (!m) {
    return (
      <div className="flex flex-col" style={{ gap: 14 }}>
        {brotkrumen}
        <FehlerKarte meldung="Mitarbeiter nicht gefunden." />
      </div>
    );
  }

  const sf = statusFarbe(m.status);
  const kopfRolle = ansicht === 'berechtigungen' ? 'Berechtigungs-Editor' : rollenName(m.role);
  const KopfIcon = ansicht === 'berechtigungen' ? LockKey : Shield;
  const datum = new Date(m.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  const zeilen: { icon: Icon; label: string; wert: string; unter?: string; fg: string; bg: string }[] = [
    { icon: EnvelopeSimple, label: 'E-Mail Adresse', wert: m.email, fg: 'var(--info)', bg: 'var(--info-soft)' },
    { icon: CalendarBlank, label: 'Mitglied seit', wert: datum, fg: 'var(--acc)', bg: 'var(--acc-soft)' },
    { icon: Fingerprint, label: 'Sicherheit', wert: '2FA Verschlüsselt', unter: 'Verifiziertes Profil', fg: 'var(--ok)', bg: 'var(--ok-soft)' },
  ];

  return (
    <>
      <div className="flex flex-col" style={{ gap: 14 }}>
        {brotkrumen}

        <section aria-label="Mitarbeiter" className="immo-glas flex flex-wrap items-center" style={{ borderRadius: 28, padding: mobil ? 18 : 22, gap: 20 }}>
          <span style={{ borderRadius: '50%', boxShadow: '0 14px 30px -12px var(--shade)' }}>
            <MitarbeiterAvatar id={m.id} name={m.full_name} url={m.avatar_url} groesse={schmal ? 72 : 96} />
          </span>
          <div className="flex min-w-0 flex-col" style={{ flex: '1 1 240px', gap: 8 }}>
            <h1 className="m-0" style={{ fontSize: schmal || mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02, overflowWrap: 'anywhere' }}>{m.full_name}</h1>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              <span className="inline-flex items-center" style={{ height: 26, padding: '0 11px', gap: 6, borderRadius: 13, fontSize: 12, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                <KopfIcon size={13} weight="fill" />{kopfRolle}
              </span>
              <span className="inline-flex items-center" style={{ height: 26, padding: '0 11px', gap: 6, borderRadius: 13, fontSize: 12, fontWeight: 800, background: sf.bg, color: sf.fg }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: sf.fg }} />{statusText(m.status)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <button type="button" onClick={() => setLoeschenOffen(true)} data-tip="Entfernen – Zugang dieses Mitarbeiters löschen" className="inline-flex items-center"
              style={{ height: 46, padding: '0 16px', gap: 7, borderRadius: 23, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
              <Trash size={15} weight="bold" />Entfernen
            </button>
            <button type="button" onClick={() => setBearbeitenOffen(true)} data-tip="Bearbeiten – Profil, Rolle und Status ändern" className="inline-flex items-center"
              style={{ height: 46, padding: '0 20px', gap: 7, borderRadius: 23, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
              <PencilSimple size={15} weight="bold" />Bearbeiten
            </button>
          </div>
        </section>

        <div role="tablist" aria-label="Ansicht" className="immo-glas flex self-start"
          onKeyDown={(e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            e.preventDefault();
            const leiste = e.currentTarget;
            zeige(ansicht === 'details' ? 'berechtigungen' : 'details');
            requestAnimationFrame(() => leiste.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
          }} style={{ gap: 4, padding: 4, borderRadius: 20, maxWidth: '100%' }}>
          {([['details', 'Details', User, 'Details – Kontakt, Mitgliedschaft und Zugriffsrechte'], ['berechtigungen', 'Berechtigungen', Shield, 'Berechtigungen – Module für diesen Mitarbeiter freischalten']] as const).map(([k, label, I, tip]) => {
            const an = ansicht === k;
            return (
              <button key={k} type="button" role="tab" aria-selected={an} tabIndex={an ? 0 : -1} data-tip={tip} onClick={() => zeige(k)} className="inline-flex items-center whitespace-nowrap"
                style={{ height: mobil ? 44 : 40, padding: '0 16px', gap: 8, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
                  background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)', boxShadow: an ? 'var(--acc-sh)' : 'none', transition: 'background .2s, color .2s' }}>
                <I size={16} weight="bold" />{label}
              </button>
            );
          })}
        </div>

        {ansicht === 'details' ? (
          <div className="grid items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 14 }}>
            <section aria-label="Kontakt & Profil" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 8 }}>
              <h2 className="m-0" style={{ padding: '10px 12px 4px', fontSize: 15, fontWeight: 800 }}>Kontakt &amp; Profil</h2>
              {zeilen.map((z, i) => {
                const I = z.icon;
                return (
                  <div key={z.label} className="flex items-center" style={{ gap: 12, padding: 12, borderTop: i ? '1px solid var(--line)' : 'none' }}>
                    <span className="flex shrink-0 items-center justify-center" style={{ width: 36, height: 36, borderRadius: 12, background: z.bg }}>
                      <I size={17} weight="fill" color={z.fg} />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>{z.label}</span>
                      <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere' }}>{z.wert}</span>
                      {z.unter && (
                        <span className="flex items-center" style={{ gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--ok)' }}>
                          <Sparkle size={10} weight="fill" />{z.unter}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
            <section aria-label="Zugriffsrechte" className="immo-glas flex items-start" style={{ borderRadius: 26, padding: 18, gap: 14 }}>
              <span className="flex shrink-0 items-center justify-center" style={{ width: 40, height: 40, borderRadius: 13, background: 'var(--ok-soft)' }}>
                <ShieldCheck size={21} weight="fill" color="var(--ok)" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 6 }}>
                <h2 className="m-0" style={{ fontSize: 15, fontWeight: 800 }}>Zugriffsrechte</h2>
                <span style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)', overflowWrap: 'anywhere' }}>{rechteText(m.allowed_modules, module)}</span>
                <button type="button" onClick={() => zeige('berechtigungen')} data-tip="Berechtigungen – Module für diesen Mitarbeiter freischalten" className="inline-flex items-center self-start"
                  style={{ marginTop: 6, height: 38, padding: '0 14px', gap: 7, borderRadius: 19, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                  <LockKey size={15} weight="bold" />Berechtigungen bearbeiten
                </button>
              </div>
            </section>
          </div>
        ) : (
          <section aria-label="Modul-Berechtigungen" className="immo-glas flex flex-col" style={{ borderRadius: 26, padding: 18, gap: 14 }}>
            <h2 className="m-0 flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
              <LockKey size={17} weight="fill" color="var(--acc)" />Modul-Berechtigungen
            </h2>
            {moduleLaden && module.length === 0 ? (
              <div className="flex flex-col items-center" style={{ padding: '36px 0', gap: 12 }}>
                <CircleNotch size={28} weight="bold" className="animate-spin" color="var(--acc)" />
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink3)' }}>Synchronisiere System-Module...</span>
              </div>
            ) : module.length > 0 ? (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 8 }}>
                {module.map((mod) => {
                  const an = erlaubt.includes(mod.id);
                  const I = MODUL_ICON[mod.id] ?? Layout;
                  return (
                    <div key={mod.id} onClick={() => umschalten(mod.id)} data-tip={`${mod.name} – Zugriff ${an ? 'entziehen' : 'erlauben'}`}
                      className="flex items-center" style={{ borderRadius: 18, padding: 12, gap: 12, cursor: 'pointer', transition: 'background .2s, box-shadow .2s',
                        background: an ? 'var(--acc-soft)' : 'var(--fill)', boxShadow: an ? 'inset 0 0 0 1.5px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
                      <span className="flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: 12, background: an ? 'var(--acc-fill)' : 'var(--fill2)' }}>
                        <I size={19} weight="fill" color={an ? 'var(--acc-ink)' : 'var(--ink3)'} />
                      </span>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{mod.name}</span>
                        <span style={{ fontSize: 11.5, color: 'var(--ink3)' }}>Zugriff erlauben</span>
                      </div>
                      <IosSchalter an={an} onChange={() => umschalten(mod.id)} label={`${mod.name} – Zugriff erlauben`} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center text-center" style={{ padding: '28px 16px', gap: 10, borderRadius: 18, background: 'var(--fill)' }}>
                <WarningCircle size={30} weight="fill" color="var(--ink3)" />
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink2)' }}>Der Super-Admin hat aktuell keine Module zur Verwaltung freigegeben.</span>
              </div>
            )}
            {fehler && (
              <div role="alert" className="flex items-center" style={{ borderRadius: 16, padding: '12px 14px', gap: 8, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
                <WarningCircle size={16} weight="fill" className="shrink-0" />{fehler}
              </div>
            )}
            <div className="flex flex-wrap justify-end" style={{ gap: 8 }}>
              <button type="button" onClick={() => zeige('details')}
                style={{ height: 46, padding: '0 18px', borderRadius: 23, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
                Abbrechen
              </button>
              <button type="button" onClick={() => void sichern()} disabled={speichert} data-tip="Berechtigungen sichern – Freigeschaltete Bereiche speichern" className="inline-flex items-center"
                style={{ height: 46, padding: '0 20px', gap: 7, borderRadius: 23, border: 'none', cursor: speichert ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
                  background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', opacity: speichert ? 0.7 : 1 }}>
                {speichert ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <FloppyDisk size={15} weight="bold" />}Berechtigungen sichern
              </button>
            </div>
          </section>
        )}
      </div>

      {loeschenOffen && (
        <MitarbeiterLoeschenDialog name={m.full_name} laeuft={loescht} onBestaetigen={() => void loeschen()} onAbbrechen={() => setLoeschenOffen(false)} />
      )}
      {bearbeitenOffen && <MitarbeiterDialog mitglied={m} onSchliessen={() => setBearbeitenOffen(false)} onErfolg={() => void laden()} />}
      {dialoge}
      <GlasTooltipSchicht />
    </>
  );
}
